import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  Alert,
  Button,
  Card,
  Col,
  Form,
  Nav,
  Modal,
  Spinner,
  Row,
  Tooltip,
  OverlayTrigger,
  Badge,
} from "react-bootstrap";
import Select from "../components/ui/AppReactSelect";
import { useDispatch, useSelector } from "react-redux";
import {
  MdEdit,
  MdDelete,
  MdList,
  MdVisibility,
  MdLocalHospital,
  MdHelpOutline,
  MdAdd,
  MdStore,
  MdSettings,
  MdTune
} from "react-icons/md";
import { FaFileImport, FaInfoCircle } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import {
  fetchHealthcareSections,
  updateHealthcareSection,
  deleteHealthcareSection,
  createHealthcareSection,
  fetchHealthcareVendors,
} from "../actions/healthcareSectionActions";
import {
  createRestaurantOption,
  createRestaurantOptionGroup,
  fetchOptionGroupsRestaurant,
  deleteOptionGroup,
  deleteOption,
  updateOption,
  updateOptionGroup,
  fetchOptionsRestaurants,
} from "../actions/optionAction.js";
import { toast } from "react-toastify";
import { apiSetCatalogItemStatus } from "../api";
import { Switch } from "@material-ui/core";
import { getUserFromLocalStorage } from "../utilities/user";
import usePersistedPreference from "../hooks/usePersistedPreference";
import { listViewDefaults } from "../config/listViewDefaults";
import placeholderImage from "../assets/img/menuImage.png";
// import OutletManager from "components/OutletManager.js";
import {
  toggleOutletActivation,
  updateOutletMenuItems,
} from "../actions/outletActions";
import OpenHoursManager from "../components/OpenHours/OpenHoursManager";
import {
  apiUpdateHealthcareOutletHours,
  apiGetHealthcareOutletHours,
} from "../api/index";
import HealthcareImportModal from "./Components/HealthcareImportModal";
import { useGate, useHeadOfficeGate, useLocationGate, Gated } from "../components/team/Gated";
import {
  photoTotals,
  photoFilterTest,
  NeedsPhotoBadge,
  PhotoFilterChips,
} from "../components/NeedsPhoto";
import AppSelect from "../components/ui/AppSelect";
import OutletMenuItemsModal from "../components/OutletMenuItemsModal";
import BranchAvailabilityGrid from "../components/branch/BranchAvailabilityGrid";
import BranchOffChip, { useBranchOffCounts } from "../components/branch/BranchOffChip";
import BranchMemberNote from "../components/branch/BranchMemberNote";
import { MY_BRANCHES_ONLY, NOT_YOUR_OUTLETS, useOutletsInScope } from "../hooks/useVendorAccess";
import { useVendorLocations } from "../context/BranchScopeContext";
import { branchAwareMessage, vendorOwnerIdOf } from "../utilities/vendorScope";

const HealthcareVendorMenu = () => {
  const user = getUserFromLocalStorage();
  // The owner's id: a team member manages the pharmacy's catalogue, not its own.
  const vendorId = vendorOwnerIdOf(user) || "";
  const sections = useSelector(
    (state) => state.healthcareSections.sectionsHealthcare
  );
  const healthcareVendors = useSelector(
    (state) => state.healthcareSections.healthcareVendors
  );
  const optionGroups = useSelector((state) => state.options.optionGroups);
  const options = useSelector((state) => state.options.options);
  const loading = useSelector((state) => state.healthcareSections.isLoading);
  const optionLoading = useSelector((state) => state.options.isLoading);
  const outlets = useSelector((state) => state.outlets?.outlets || []);
  const outletLoading = useSelector((state) => state.outlets?.isLoading || false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  // Vendor team permissions: sections, products, options and import are
  // catalogue:edit; switching a product on or off is catalogue:activate.
  const catalogueGate = useGate("catalogue:edit");
  const activateGate = useGate("catalogue:activate");
  // Pharmacy outlets (4 Oct 2026): a branch's switch and hours are that
  // branch's to run; what it sells is head office's.
  const outletAt = useLocationGate("outlets:manage");
  const outletProductsGate = useHeadOfficeGate("outlets:manage");
  // The Branches tab and the "Off at n of m branches" chip.
  const branchDirectory = useVendorLocations(vendorId || null);
  const branchOff = useBranchOffCounts(vendorId || null);
  // The pharmacy's branches this login runs (a branch member: theirs only).
  const { outlets: myOutlets, limited: outletsLimited } = useOutletsInScope(healthcareVendors?.[0]?.outlets);
  const [showEditModal, setShowEditModal] = useState(false);
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionDescription, setSectionDescription] = useState("");
  const [selectedHealthcareId, setSelectedHealthcareId] = useState("");
  const [activeKey, setActiveKey] = useState([]);
  const [currentSectionId, setCurrentSectionId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [activeNav, setActiveNav] = useState("menu-overview");

  const [showOptionModal, setShowOptionModal] = useState(false);
  const [optionName, setOptionName] = useState("");
  const [optionPrice, setOptionPrice] = useState(0);

  const [showOptionGroupModal, setShowOptionGroupModal] = useState(false);
  const [optionGroupName, setOptionGroupName] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [minOptions, setMinOptions] = useState(0);
  const [maxOptions, setMaxOptions] = useState(0);
  const [currentOptionGroupId, setCurrentOptionGroupId] = useState(null);

  const [showDeleteOptionGroupModal, setShowDeleteOptionGroupModal] = useState(false);
  const [optionGroupToDelete, setOptionGroupToDelete] = useState(null);
  const [showOptionDeleteModal, setShowOptionDeleteModal] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState(null);

  const [showEditOptionModal, setShowEditOptionModal] = useState(false);
  const [currentOptionId, setCurrentOptionId] = useState(null);
  const [editOptionName, setEditOptionName] = useState("");
  const [editOptionPrice, setEditOptionPrice] = useState(0);
  const [editOptionEnabled, setEditOptionEnabled] = useState(true);

  const [productSearchQuery, setProductSearchQuery] = useState("");
  /* null shows every product; otherwise a PHOTO_FILTERS key.
     The pharmacy importer stamps `imageSource` on a row brought in ahead of
     its photography, and both the shared review modal and the pharmacy
     workbook template tell the operator those rows "will be marked Needs
     photo on Lusuku" and can be filtered for. Until this chip existed that
     was a promise with nothing behind it on healthcare: the mark was written
     on import and read by no pharmacy screen, so a stand-in cover looked
     exactly like a real one and the marking bought the operator nothing.
     The rule, the badge and these chips all come from components/NeedsPhoto,
     shared with the brand console so the two can never disagree about what
     counts as a stand-in. */
  /* Persisted per user, like the brand console's chips: opening a product
     unmounts this page, and the chip used to come back off. First value by
     role from config/listViewDefaults. */
  const [photoFilter, setPhotoFilter] = usePersistedPreference(
    "healthcareMenu.photoFilter",
    listViewDefaults("healthcareMenu", user?.result?.role).photoFilter,
    { perUser: true }
  );
  const [hoveredImage, setHoveredImage] = useState(null);
  const [imagePreviewPosition, setImagePreviewPosition] = useState({ x: 0, y: 0 });

  const [showOutletProductsModal, setShowOutletProductsModal] = useState(false);
  // An hours save in flight. It used to borrow the product picker's flag,
  // which moved into the shared picker (4 Oct 2026).
  const [isSavingOutletHours, setIsSavingOutletHours] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(null);

  const [showOutletHoursModal, setShowOutletHoursModal] = useState(false);
  const [selectedOutletForHours, setSelectedOutletForHours] = useState(null);
  const [outletHours, setOutletHours] = useState(null);
  const [isLoadingOutletHours, setIsLoadingOutletHours] = useState(false);

  const [showOutletConfirmModal, setShowOutletConfirmModal] = useState(false);
  const [outletToToggle, setOutletToToggle] = useState(null);
  const [isTogglingOutlet, setIsTogglingOutlet] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    const isSuperAdmin = user?.result?.user_role === "SUPERUSER";
    dispatch(fetchHealthcareVendors(vendorId, isSuperAdmin));
  }, [dispatch, vendorId]);

  // Fetch sections using healthcareId once vendors are loaded
  useEffect(() => {
    if (healthcareVendors.length > 0) {
      const healthcareId = healthcareVendors[0]?.healthcareId;
      if (healthcareId) {
        dispatch(fetchHealthcareSections(healthcareId));
      }
    }
  }, [dispatch, healthcareVendors]);

  useEffect(() => {
    if (vendorId) {
      dispatch(fetchOptionGroupsRestaurant(vendorId));
      dispatch(fetchOptionsRestaurants(vendorId));
    }
  }, [dispatch, vendorId]);

  useEffect(() => {
    if (sections.length > 0) {
      setActiveKey(sections.map((section) => String(section._id)));
    }
  }, [sections]);

  /* Every product in the catalogue, for the chip counts. Counted over the
     whole catalogue rather than the filtered view, so pressing a chip never
     changes the number written on it. */
  const catalogueProducts = useMemo(
    () => (Array.isArray(sections) ? sections : []).flatMap((s) => s.products || []),
    [sections]
  );
  const totals = useMemo(
    () => photoTotals(catalogueProducts),
    [catalogueProducts]
  );

  /* Sections with their products filtered. A section with no surviving row is
     dropped rather than shown empty: "Antibiotics (0 items)" reads as a
     section somebody emptied, not as a filter doing its job. */
  const visibleSections = useMemo(() => {
    const photoTest = photoFilterTest(photoFilter);
    const all = Array.isArray(sections) ? sections : [];
    if (!photoTest) return all;
    return all
      .map((section) => ({
        ...section,
        products: (section.products || []).filter(photoTest),
      }))
      .filter((section) => section.products.length > 0);
  }, [sections, photoFilter]);

  /* While a filter is on, every surviving section opens. A closed accordion
     hiding the one row the operator filtered for is a filter that did
     nothing. */
  useEffect(() => {
    if (!photoFilter) return;
    setActiveKey(visibleSections.map((section) => String(section._id)));
  }, [photoFilter, visibleSections]);

  const handleMenuSectionSubmit = (e) => {
    e.preventDefault();
    dispatch(
      createHealthcareSection(
        {
          title: sectionTitle,
          description: sectionDescription,
          healthcareId: selectedHealthcareId,
        },
        vendorId
      )
    );
    setShowCreateModal(false);
    setSectionTitle("");
    setSectionDescription("");
    setSelectedHealthcareId("");
  };

  const handleSectionEditSubmit = (e) => {
    e.preventDefault();
    dispatch(
      updateHealthcareSection(currentSectionId, {
        title: sectionTitle,
        description: sectionDescription,
        healthcareId: selectedHealthcareId,
      })
    );
    setShowEditModal(false);
    setSectionTitle("");
    setSectionDescription("");
    setSelectedHealthcareId("");
    setCurrentSectionId(null);
  };

  const handleEditSection = (section) => {
    setSectionTitle(section.title);
    setSectionDescription(section.description);
    setSelectedHealthcareId(
      healthcareVendors.find((v) => v.healthcareId === section.healthcareId)
        ?.healthcareId || ""
    );
    setCurrentSectionId(section._id);
    setShowEditModal(true);
  };

  const handleDeleteSection = (section) => {
    setSectionToDelete(section);
    setShowDeleteModal(true);
  };

  const confirmDeleteSection = () => {
    if (sectionToDelete) {
      const healthcareId = healthcareVendors[0]?.healthcareId;
      dispatch(deleteHealthcareSection(healthcareId, sectionToDelete._id));
      setShowDeleteModal(false);
      setSectionToDelete(null);
    }
  };

  const handleToggleAccordion = (eventKey) => {
    setActiveKey((prevKeys) => {
      if (prevKeys.includes(eventKey)) {
        return prevKeys.filter((key) => key !== eventKey);
      } else {
        return [...prevKeys, eventKey];
      }
    });
  };

  const handleCreateProduct = (sectionId) => {
    history.push({
      pathname: "/admin/create-healthcare-product",
      state: { sectionId },
    });
  };

  const handleEditProduct = (productId) => {
    history.push(`/admin/edit-healthcare-product/${productId}`);
  };

  // Quick per-item activate/deactivate — unified endpoint; storefronts update
  // live via the catalog:public `product:statusChanged` socket event.
  const handleToggleProductStatus = async (e, product) => {
    e.stopPropagation();
    const next = !product.enabled;
    try {
      await apiSetCatalogItemStatus("healthcare", product._id, next);
      toast.success(`"${product.name}" ${next ? "activated" : "deactivated"}`);
      const healthcareId = healthcareVendors[0]?.healthcareId;
      if (healthcareId) dispatch(fetchHealthcareSections(healthcareId));
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to change product status"
      );
    }
  };

  const handleOptionSubmit = (e) => {
    e.preventDefault();
    dispatch(
      createRestaurantOption({
        name: optionName,
        price: optionPrice,
        owner: vendorId,
      })
    );
    setShowOptionModal(false);
    resetOptionForm();
  };

  const handleOptionGroupSubmit = async (e) => {
    e.preventDefault();

    const optionGroupData = {
      name: optionGroupName,
      restaurantProductId: selectedHealthcareId,
      options: selectedOptions.map((option) => ({
        value: option.value,
        mandatory: minOptions > 0,
      })),
      minOptions,
      maxOptions,
      owner: vendorId,
    };

    if (currentOptionGroupId) {
      await dispatch(updateOptionGroup(currentOptionGroupId, optionGroupData));
    } else {
      await dispatch(createRestaurantOptionGroup(optionGroupData));
    }

    dispatch(fetchOptionGroupsRestaurant(vendorId));

    setShowOptionGroupModal(false);
    resetOptionGroupForm();
  };

  const handleEditOptionGroup = (optionGroup) => {
    setCurrentOptionGroupId(optionGroup._id);
    setOptionGroupName(optionGroup.name);
    setSelectedOptions(
      optionGroup.options
        .map((option) => {
          if (option.option && option.option._id) {
            return {
              value: option.option._id,
              label: `${option.option.name} - ${new Intl.NumberFormat("en-UG", {
                style: "currency",
                currency: "UGX",
              }).format(option.option.price)}`,
              mandatory: option.mandatory,
            };
          }
          return null;
        })
        .filter((option) => option !== null)
    );
    setMinOptions(optionGroup.minOptions);
    setMaxOptions(optionGroup.maxOptions);
    setSelectedHealthcareId(optionGroup.restaurantId || "");
    setShowOptionGroupModal(true);
  };

  const handleDeleteOption = (optionId) => {
    setOptionToDelete(optionId);
    setShowOptionDeleteModal(true);
  };

  const confirmDeleteOption = async () => {
    try {
      await dispatch(deleteOption(optionToDelete));
      setShowOptionDeleteModal(false);
      setOptionToDelete(null);
    } catch (error) {
      toast.error("Failed to delete the option. Please try again.");
    }
  };

  const handleDeleteOptionGroup = (optionGroupId) => {
    setOptionGroupToDelete(optionGroupId);
    setShowDeleteOptionGroupModal(true);
  };

  const confirmDeleteOptionGroup = async () => {
    try {
      await dispatch(deleteOptionGroup(optionGroupToDelete));
      setShowDeleteOptionGroupModal(false);
      setOptionGroupToDelete(null);
    } catch (error) {
      toast.error("Failed to delete the option group. Please try again.");
    }
  };

  const handleEditOption = (option) => {
    setCurrentOptionId(option._id);
    setEditOptionName(option.name);
    setEditOptionPrice(option.price);
    setEditOptionEnabled(option.enabled);
    setShowEditOptionModal(true);
  };

  const handleEditOptionSubmit = (e) => {
    e.preventDefault();
    const updatedOption = {
      name: editOptionName,
      price: editOptionPrice,
      enabled: editOptionEnabled,
    };
    dispatch(updateOption(currentOptionId, updatedOption));
    setShowEditOptionModal(false);
    resetEditOptionForm();
  };

  const handleCloseOptionGroupModal = () => {
    resetOptionGroupForm();
    setShowOptionGroupModal(false);
  };

  const resetOptionForm = () => {
    setOptionName("");
    setOptionPrice(0);
  };

  const resetOptionGroupForm = () => {
    setOptionGroupName("");
    setSelectedHealthcareId("");
    setSelectedOptions([]);
    setMinOptions(0);
    setMaxOptions(0);
    setCurrentOptionGroupId(null);
  };

  const resetEditOptionForm = () => {
    setEditOptionName("");
    setEditOptionPrice(0);
    setCurrentOptionId(null);
  };

  // What an outlet sells: the shared picker (components/OutletMenuItemsModal)
  // owns the selection; the action toasts once and rethrows, which keeps the
  // picker open on a refusal.
  const handleManageOutletProducts = (outlet) => {
    setSelectedOutlet(outlet);
    setShowOutletProductsModal(true);
  };

  const handleSaveOutletProducts = async (productIds) => {
    if (!selectedOutlet) return;
    await dispatch(updateOutletMenuItems(selectedOutlet._id, productIds, { vertical: "healthcare" }));
    const isSuperAdmin = user?.result?.user_role === "SUPERUSER";
    dispatch(fetchHealthcareVendors(vendorId, isSuperAdmin));
  };

  const handleManageOutletHours = async (outlet) => {
    setSelectedOutletForHours(outlet);
    setShowOutletHoursModal(true);
    setIsLoadingOutletHours(true);
    try {
      // Fetch outlet-specific hours
      const response = await apiGetHealthcareOutletHours(outlet._id);
      setOutletHours(response.data || {
        openHours: outlet.openHours || null,
        specialHours: outlet.specialHours || [],
        timezone: outlet.timezone || "Africa/Kampala",
      });
    } catch (error) {
      console.error("Error fetching outlet hours:", error);
      // Fallback to outlet's current hours
      setOutletHours({
        openHours: outlet.openHours || null,
        specialHours: outlet.specialHours || [],
        timezone: outlet.timezone || "Africa/Kampala",
      });
    } finally {
      setIsLoadingOutletHours(false);
    }
  };

  const handleSaveOutletHours = async (hoursData) => {
    if (!selectedOutletForHours) return;
    setIsSavingOutletHours(true);
    try {
      await apiUpdateHealthcareOutletHours(selectedOutletForHours._id, hoursData);
      toast.success(`Business hours updated for ${selectedOutletForHours.name}!`);
      setShowOutletHoursModal(false);
      
      // Refresh healthcare vendors list to reflect changes
      const isSuperAdmin = user?.result?.user_role === "SUPERUSER";
      dispatch(fetchHealthcareVendors(vendorId, isSuperAdmin));
    } catch (error) {
      console.error("Error saving outlet hours:", error);
      toast.error(branchAwareMessage(error, "Failed to update business hours"));
    } finally {
      setIsSavingOutletHours(false);
    }
  };

  const handleToggleOutletActivation = (outlet) => {
    setOutletToToggle(outlet);
    setShowOutletConfirmModal(true);
  };

  const confirmToggleOutletActivation = async () => {
    if (!outletToToggle) return;
    setIsTogglingOutlet(true);
    try {
      const newStatus = !outletToToggle.isActive;
      // The action toasts the outcome once.
      await dispatch(toggleOutletActivation(outletToToggle._id, newStatus, { vertical: "healthcare" }));

      // Refresh healthcare vendors list
      const isSuperAdmin = user?.result?.user_role === "SUPERUSER";
      dispatch(fetchHealthcareVendors(vendorId, isSuperAdmin));
      setShowOutletConfirmModal(false);
      setOutletToToggle(null);
    } catch (error) {
      console.error("Error toggling outlet activation:", error);
    } finally {
      setIsTogglingOutlet(false);
    }
  };


  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <Card.Header
              style={{
                background: "linear-gradient(135deg, #E91E63 0%, #C2185B 100%)",
                color: "white",
                padding: "24px",
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="d-flex align-items-center mb-2">
                    <MdLocalHospital
                      size={32}
                      style={{ marginRight: "12px" }}
                    />
                    <Card.Title
                      as="h3"
                      style={{
                        marginBottom: 0,
                        fontSize: "28px",
                        fontWeight: "700",
                        color: "white",
                      }}
                    >
                      Healthcare Menu Management
                    </Card.Title>
                  </div>
                  <p
                    style={{ marginBottom: 0, opacity: 0.9, fontSize: "14px" }}
                  >
                    Manage your healthcare sections, products & services
                  </p>
                </div>
                {/* Two actions of unequal weight, so they get unequal
                    treatment: creating a section is what most visits are for
                    and reads as the primary; the bulk import is the
                    occasional path. Ranking them stops the row wrapping into
                    two lines of identical buttons. */}
                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                  <Gated perm="catalogue:edit">
                  <Button
                    size="sm"
                    onClick={() => setShowImportModal(true)}
                    title="Bulk upload products and services from a filled workbook"
                    style={{
                      fontWeight: "600",
                      background: "transparent",
                      border: "1px solid rgba(255, 255, 255, 0.8)",
                      color: "white",
                    }}
                  >
                    <FaFileImport size={13} style={{ marginRight: "6px" }} />
                    Import
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowCreateModal(true)}
                    style={{ fontWeight: "600" }}
                  >
                    + Create Section
                  </Button>
                  </Gated>
                </div>
              </div>
            </Card.Header>
            <Nav
              variant="tabs"
              activeKey={activeNav}
              onSelect={(selectedKey) => setActiveNav(selectedKey)}
              style={{
                borderBottom: "2px solid #e9ecef",
                background: "white",
                padding: "8px 16px 0 16px",
                position: "sticky",
                top: 0,
                zIndex: 1000,
                overflowX: "auto",
                whiteSpace: "nowrap",
                display: "flex",
                flexWrap: "nowrap",
              }}
            >
              <Nav.Item style={{ flexShrink: 0 }}>
                <Nav.Link
                  eventKey="menu-overview"
                  style={{
                    background:
                      activeNav === "menu-overview" ? "#E91E63" : "transparent",
                    color: activeNav === "menu-overview" ? "white" : "#495057",
                    borderRadius: "8px 8px 0 0",
                    margin: "0 4px",
                    fontWeight: "500",
                    fontSize: "14px",
                    padding: "10px 20px",
                    border: "none",
                  }}
                >
                  <MdList
                    size={16}
                    style={{ marginRight: "6px", verticalAlign: "middle" }}
                  />
                  Menu Overview
                </Nav.Link>
              </Nav.Item>
              <Nav.Item style={{ flexShrink: 0 }}>
                <Nav.Link
                  eventKey="outlets"
                  style={{
                    background:
                      activeNav === "outlets" ? "#E91E63" : "transparent",
                    color: activeNav === "outlets" ? "white" : "#495057",
                    borderRadius: "8px 8px 0 0",
                    margin: "0 4px",
                    fontWeight: "500",
                    fontSize: "14px",
                    padding: "10px 20px",
                    border: "none",
                  }}
                >
                  <MdStore
                    size={16}
                    style={{ marginRight: "6px", verticalAlign: "middle" }}
                  />
                  Outlets
                </Nav.Link>
              </Nav.Item>
              <Nav.Item style={{ flexShrink: 0 }}>
                <Nav.Link
                  eventKey="option-groups"
                  style={{
                    background:
                      activeNav === "option-groups" ? "#E91E63" : "transparent",
                    color: activeNav === "option-groups" ? "white" : "#495057",
                    borderRadius: "8px 8px 0 0",
                    margin: "0 4px",
                    fontWeight: "500",
                    fontSize: "14px",
                    padding: "10px 20px",
                    border: "none",
                  }}
                >
                  <MdTune
                    size={16}
                    style={{ marginRight: "6px", verticalAlign: "middle" }}
                  />
                  Option Groups
                </Nav.Link>
              </Nav.Item>
              <Nav.Item style={{ flexShrink: 0 }}>
                <Nav.Link
                  eventKey="options"
                  style={{
                    background:
                      activeNav === "options" ? "#E91E63" : "transparent",
                    color: activeNav === "options" ? "white" : "#495057",
                    borderRadius: "8px 8px 0 0",
                    margin: "0 4px",
                    fontWeight: "500",
                    fontSize: "14px",
                    padding: "10px 20px",
                    border: "none",
                  }}
                >
                  <MdSettings
                    size={16}
                    style={{ marginRight: "6px", verticalAlign: "middle" }}
                  />
                  Options
                </Nav.Link>
              </Nav.Item>
              <Nav.Item style={{ flexShrink: 0 }}>
                <Nav.Link
                  eventKey="simulation"
                  style={{
                    background:
                      activeNav === "simulation" ? "#E91E63" : "transparent",
                    color: activeNav === "simulation" ? "white" : "#495057",
                    borderRadius: "8px 8px 0 0",
                    margin: "0 4px",
                    fontWeight: "500",
                    fontSize: "14px",
                    padding: "10px 20px",
                    border: "none",
                  }}
                >
                  <MdVisibility
                    size={16}
                    style={{ marginRight: "6px", verticalAlign: "middle" }}
                  />
                  Simulation
                </Nav.Link>
              </Nav.Item>
              {branchDirectory.hasBranches && (
                <Nav.Item style={{ flexShrink: 0 }}>
                  <Nav.Link
                    eventKey="branches"
                    style={{
                      background: activeNav === "branches" ? "#E91E63" : "transparent",
                      color: activeNav === "branches" ? "white" : "#495057",
                      borderRadius: "8px 8px 0 0",
                      margin: "0 4px",
                      fontWeight: "500",
                      fontSize: "14px",
                      padding: "10px 20px",
                      border: "none",
                    }}
                  >
                    <MdStore size={16} style={{ marginRight: "6px", verticalAlign: "middle" }} />
                    Branches
                  </Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item style={{ flexShrink: 0 }}>
                <Nav.Link
                  eventKey="how-it-works"
                  style={{
                    background:
                      activeNav === "how-it-works" ? "#E91E63" : "transparent",
                    color: activeNav === "how-it-works" ? "white" : "#495057",
                    borderRadius: "8px 8px 0 0",
                    margin: "0 4px",
                    fontWeight: "500",
                    fontSize: "14px",
                    padding: "10px 20px",
                    border: "none",
                  }}
                >
                  <MdHelpOutline
                    size={16}
                    style={{ marginRight: "6px", verticalAlign: "middle" }}
                  />
                  How it works
                </Nav.Link>
              </Nav.Item>
            </Nav>
            <Card.Body>
              {loading && sections.length === 0 ? (
                <div className="text-center">Loading...</div>
              ) : (
                <>
                  {activeNav === "menu-overview" && branchDirectory.hasBranches && (
                    <BranchMemberNote onOpenBranches={() => setActiveNav("branches")} />
                  )}
                  {activeNav === "menu-overview" &&
                    (sections.length === 0 ? (
                      <div className="text-center text-muted">
                        <p>No sections created yet</p>
                        <Gated perm="catalogue:edit">
                        <Button
                          variant="primary"
                          onClick={() => setShowCreateModal(true)}
                        >
                          Create Your First Section
                        </Button>
                        </Gated>
                      </div>
                    ) : (
                      <>
                        {/* ── Summary stat strip ── */}
                        {(() => {
                          const chips = [
                            { label: "Sections",         value: sections.length,                              bg: "#e3f2fd", color: "#1565c0", border: "#90caf9" },
                            { label: "Total Listings",    value: catalogueProducts.length,                           bg: "#f3e5f5", color: "#6a1b9a", border: "#ce93d8" },
                            { label: "Active Listings",   value: catalogueProducts.filter((p) => p.enabled).length,  bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" },
                            { label: "Inactive Listings", value: catalogueProducts.filter((p) => !p.enabled).length, bg: "#fff3e0", color: "#e65100", border: "#ffcc80" },
                          ];
                          return (
                            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "18px" }}>
                              {chips.map(({ label, value, bg, color, border }) => (
                                <div key={label} style={{ background: bg, border: `1.5px solid ${border}`, borderRadius: "10px", padding: "10px 18px", minWidth: "110px", textAlign: "center" }}>
                                  <div style={{ fontSize: "1.4rem", fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
                                  <div style={{ fontSize: "0.72rem", fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: "3px" }}>{label}</div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}

                        {/* Photography chips. Hidden until the catalogue has
                            at least one imported stand-in cover, so a pharmacy
                            that has never used the importer is not shown a
                            control that can only ever say zero. The chips and
                            the rule behind them are the brand console's, from
                            components/NeedsPhoto. */}
                        {totals.needsPhoto > 0 && (
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "18px" }}>
                            <PhotoFilterChips
                              value={photoFilter}
                              onChange={setPhotoFilter}
                              totals={totals}
                            />
                          </div>
                        )}

                      <Accordion
                        activeKey={activeKey}
                        alwaysOpen
                        onSelect={handleToggleAccordion}
                      >
                        {visibleSections.map((section) => (
                          <Accordion.Item
                            eventKey={String(section._id)}
                            key={section._id}
                          >
                            <Accordion.Header>
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  paddingRight: "10px",
                                }}
                              >
                                <div>
                                  <strong>{section.title}</strong>
                                  <Badge bg="secondary" className="ms-2">
                                    {section.products?.length || 0} items
                                  </Badge>
                                </div>
                                {catalogueGate.allowed && (
                                <div
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ display: "flex", gap: "8px" }}
                                >
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditSection(section);
                                    }}
                                    style={{
                                      cursor: "pointer",
                                      color: "#007bff",
                                      padding: "4px 8px",
                                      display: "inline-flex",
                                      alignItems: "center",
                                    }}
                                    title="Edit section"
                                  >
                                    <MdEdit size={18} />
                                  </span>
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteSection(section);
                                    }}
                                    style={{
                                      cursor: "pointer",
                                      color: "#dc3545",
                                      padding: "4px 8px",
                                      display: "inline-flex",
                                      alignItems: "center",
                                    }}
                                    title="Delete section"
                                  >
                                    <MdDelete size={18} />
                                  </span>
                                </div>
                                )}
                              </div>
                            </Accordion.Header>
                            <Accordion.Body>
                              {section.description && (
                                <p className="text-muted">
                                  {section.description}
                                </p>
                              )}

                              <Gated perm="catalogue:edit">
                              <Button
                                variant="success"
                                size="sm"
                                className="mb-3"
                                onClick={() => handleCreateProduct(section._id)}
                              >
                                + Add Product/Service
                              </Button>
                              </Gated>

                              {section.products &&
                              section.products.length > 0 ? (
                                <div className="table-responsive">
                                  <table className="table table-hover">
                                    <thead>
                                      <tr>
                                        <th>Name</th>
                                        <th>Type</th>
                                        <th>Price</th>
                                        <th>Status</th>
                                        <th>Flags</th>
                                        <th>Stock/Slots</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {section.products.map((product) => (
                                        <tr key={product._id}>
                                          <td>
                                            <div className="d-flex align-items-center">
                                              {product.image && (
                                                <div
                                                  style={{ marginRight: "10px", flexShrink: 0, cursor: "zoom-in" }}
                                                  onMouseEnter={(e) => {
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    setHoveredImage(product.image || placeholderImage);
                                                    setImagePreviewPosition({ x: rect.left + rect.width + 16, y: rect.top });
                                                  }}
                                                  onMouseLeave={() => setHoveredImage(null)}
                                                >
                                                  <img
                                                    src={product.image}
                                                    alt={product.name}
                                                    style={{
                                                      width: "40px",
                                                      height: "40px",
                                                      objectFit: "cover",
                                                      borderRadius: "4px",
                                                      display: "block",
                                                    }}
                                                  />
                                                </div>
                                              )}
                                              <span>
                                                {product.name}
                                                {/* The mark the pharmacy
                                                    importer promised. Same
                                                    component the brand
                                                    console renders. */}
                                                <NeedsPhotoBadge product={product} />
                                                <BranchOffChip
                                                  count={branchOff.counts.get(String(product._id))}
                                                  total={branchOff.total}
                                                />
                                              </span>
                                            </div>
                                          </td>
                                          <td>
                                            <Badge
                                              bg={
                                                product.productType ===
                                                "SERVICE"
                                                  ? "info"
                                                  : "primary"
                                              }
                                            >
                                              {product.productType || "PRODUCT"}
                                            </Badge>
                                          </td>
                                          <td>
                                            UGX{" "}
                                            {product.price?.toLocaleString()}
                                          </td>
                                          <td>
                                            <span
                                              onClick={(e) => e.stopPropagation()}
                                              title={
                                                !activateGate.allowed
                                                  ? activateGate.title
                                                  : product.enabled
                                                    ? "Deactivate this item (hides it from shoppers instantly)"
                                                    : "Activate this item (shoppers see it instantly)"
                                              }
                                            >
                                              <Switch
                                                size="small"
                                                checked={!!product.enabled}
                                                disabled={!activateGate.allowed}
                                                onChange={(e) =>
                                                  handleToggleProductStatus(e, product)
                                                }
                                                color="primary"
                                              />
                                            </span>
                                            <Badge
                                              bg={
                                                product.enabled
                                                  ? "success"
                                                  : "secondary"
                                              }
                                            >
                                              {product.enabled
                                                ? "Active"
                                                : "Disabled"}
                                            </Badge>
                                          </td>
                                          <td>
                                            <div
                                              style={{
                                                display: "flex",
                                                gap: "4px",
                                                flexWrap: "wrap",
                                              }}
                                            >
                                              {product.requiresPrescription && (
                                                <Badge
                                                  bg="danger"
                                                  style={{ fontSize: "10px" }}
                                                >
                                                  Rx
                                                </Badge>
                                              )}
                                              {product.ageRestriction > 0 && (
                                                <Badge
                                                  bg="warning"
                                                  text="dark"
                                                  style={{ fontSize: "10px" }}
                                                >
                                                  {product.ageRestriction}+
                                                </Badge>
                                              )}
                                              {product.controlledSubstance && (
                                                <Badge
                                                  bg="dark"
                                                  style={{ fontSize: "10px" }}
                                                >
                                                  Controlled
                                                </Badge>
                                              )}
                                            </div>
                                          </td>
                                          <td>
                                            {product.productType === "SERVICE"
                                              ? `${
                                                  product.availableSlots || 0
                                                } slots`
                                              : `${
                                                  product.countInStock || 0
                                                } units`}
                                          </td>
                                          <td>
                                            <Button
                                              variant="link"
                                              size="sm"
                                              onClick={() =>
                                                handleEditProduct(product._id)
                                              }
                                            >
                                              <MdEdit size={18} />
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-center text-muted py-3">
                                  <p>No products or services in this section</p>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    {...catalogueGate.disabledProps}
                                    onClick={() =>
                                      handleCreateProduct(section._id)
                                    }
                                  >
                                    Add First Item
                                  </Button>
                                </div>
                              )}
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>

                      {/* A filter can outlive the rows it matched: the last
                          stand-in cover gets replaced while the chip is still
                          pressed, and the catalogue would otherwise vanish
                          with no explanation. Say so, and offer the way back. */}
                      {photoFilter && visibleSections.length === 0 && (
                        <div className="text-center text-muted py-4">
                          <p style={{ marginBottom: "10px" }}>
                            No products match this photo filter.
                          </p>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setPhotoFilter(null)}
                          >
                            Show all products
                          </Button>
                        </div>
                      )}
                      </>
                    ))}
                  {activeNav === "outlets" && healthcareVendors.length > 0 && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                          <h4 style={{ fontWeight: "700", color: "#E91E63", marginBottom: "4px" }}>
                            Outlets Management
                          </h4>
                          <p style={{ color: "#6c757d", fontSize: "14px", marginBottom: 0 }}>
                            {outletsLimited ? MY_BRANCHES_ONLY : "Manage your healthcare service outlets/branches"}
                          </p>
                        </div>
                      </div>

                      {myOutlets.length > 0 ? (
                        <Row className="g-4">
                          {myOutlets.map((outlet) => (
                            <Col md={6} lg={4} key={outlet._id} className="mb-3">
                              <Card
                                style={{
                                  border: "1px solid #e9ecef",
                                  borderRadius: "14px",
                                  transition: "all 0.3s ease",
                                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                                  overflow: "hidden",
                                  height: "100%",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = "translateY(-4px)";
                                  e.currentTarget.style.boxShadow = "0 8px 20px rgba(0, 0, 0, 0.12)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "translateY(0)";
                                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.04)";
                                }}
                              >
                                <Card.Body style={{ padding: "20px" }}>
                                  <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div>
                                      <h5 style={{
                                        fontWeight: "600",
                                        fontSize: "16px",
                                        marginBottom: "4px",
                                        color: "#1a1a2e"
                                      }}>
                                        {outlet.name}
                                      </h5>
                                      <div style={{
                                        fontSize: "13px",
                                        color: "#6c757d",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px"
                                      }}>
                                        <MdStore size={14} />
                                        {outlet.address}
                                      </div>
                                    </div>
                                    <Form.Check
                                      type="switch"
                                      id={`outlet-switch-${outlet._id}`}
                                      checked={outlet.isActive !== false}
                                      onChange={() => handleToggleOutletActivation(outlet)}
                                      disabled={isTogglingOutlet || !outletAt(outlet._id).allowed}
                                      title={outletAt(outlet._id).title}
                                    />
                                  </div>

                                  <div style={{
                                    background: "#f8f9fa",
                                    borderRadius: "10px",
                                    padding: "12px",
                                    fontSize: "13px",
                                    color: "#495057",
                                    marginBottom: "12px"
                                  }}>
                                    <div className="d-flex justify-content-between mb-2">
                                      <span>Delivery Radius:</span>
                                      <strong>{outlet.deliveryZoneRadius} km</strong>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                      <span>Products:</span>
                                      <strong>{outlet.menuItems?.length || 0} items</strong>
                                    </div>
                                  </div>

                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <Badge
                                      bg={outlet.isActive !== false ? "success" : "secondary"}
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        padding: "6px 10px",
                                        borderRadius: "6px"
                                      }}
                                    >
                                      {outlet.isActive !== false ? "Active" : "Inactive"}
                                    </Badge>
                                  </div>

                                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      style={{
                                        borderRadius: "8px",
                                        fontWeight: "500",
                                        fontSize: "13px",
                                      }}
                                      onClick={() => handleManageOutletProducts(outlet)}
                                      disabled={!outletProductsGate.allowed}
                                      title={outletProductsGate.title}
                                    >
                                      <MdEdit size={14} style={{ marginRight: "4px" }} />
                                      Manage Products
                                    </Button>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      style={{
                                        borderRadius: "8px",
                                        fontWeight: "500",
                                        fontSize: "13px",
                                      }}
                                      onClick={() => handleManageOutletHours(outlet)}
                                      disabled={!outletAt(outlet._id).allowed}
                                      title={outletAt(outlet._id).title}
                                    >
                                      <MdSettings size={14} style={{ marginRight: "4px" }} />
                                      Business Hours
                                    </Button>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      ) : (
                        <div style={{ textAlign: "center", padding: "60px 20px", color: "#6c757d" }}>
                          <MdStore size={48} style={{ marginBottom: "12px", opacity: 0.5 }} />
                          <h5 style={{ fontWeight: "600" }}>No Outlets Yet</h5>
                          <p>
                            {outletsLimited && (healthcareVendors?.[0]?.outlets || []).length > 0
                              ? NOT_YOUR_OUTLETS
                              : "Contact support to add outlets for your healthcare services"}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  {activeNav === "option-groups" && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                          <h4 style={{ fontWeight: "700", color: "#E91E63", marginBottom: "4px" }}>
                            Option Groups
                          </h4>
                          <p style={{ color: "#6c757d", fontSize: "14px", marginBottom: 0 }}>
                            Create customization groups for your products (e.g., "Add-ons", "Dosage", "Quantity")
                          </p>
                        </div>
                        <Button
                          variant="primary"
                          onClick={() => setShowOptionGroupModal(true)}
                          disabled={optionLoading || !catalogueGate.allowed}
                          title={catalogueGate.title}
                          style={{
                            background: "linear-gradient(135deg, #E91E63 0%, #C2185B 100%)",
                            border: "none",
                            boxShadow: "0 4px 12px rgba(233, 30, 99, 0.3)",
                          }}
                        >
                          {optionLoading ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <>
                              <MdAdd size={18} style={{ marginRight: "6px", verticalAlign: "middle" }} />
                              Add Option Group
                            </>
                          )}
                        </Button>
                      </div>
                      
                      {optionGroups.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "60px 20px", color: "#6c757d" }}>
                          <MdTune size={48} style={{ marginBottom: "12px", opacity: 0.5 }} />
                          <h5 style={{ fontWeight: "600" }}>No Option Groups Yet</h5>
                          <p>Create option groups to add customizations to your products</p>
                        </div>
                      ) : (
                        <Row>
                          {optionGroups.map((optionGroup) => {
                            const isPickOne = optionGroup.minOptions === 1 && optionGroup.maxOptions === 1;
                            const isRequired = optionGroup.minOptions > 0;
                            const optionCount = optionGroup.options?.length || 0;
                            
                            return (
                              <Col md={6} lg={4} key={optionGroup._id} className="mb-3">
                                <Card
                                  onClick={() => handleEditOptionGroup(optionGroup)}
                                  style={{
                                    cursor: "pointer",
                                    border: "1px solid #e9ecef",
                                    borderRadius: "16px",
                                    transition: "all 0.3s ease",
                                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                                    overflow: "hidden",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-4px)";
                                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.12)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.06)";
                                  }}
                                >
                                  <Card.Body style={{ padding: "20px" }}>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                      <div
                                        style={{
                                          width: "44px",
                                          height: "44px",
                                          borderRadius: "12px",
                                          background: isPickOne 
                                            ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" 
                                            : "linear-gradient(135deg, #E91E63 0%, #C2185B 100%)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color: "white",
                                        }}
                                      >
                                        <MdTune size={22} />
                                      </div>
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={
                                          <Tooltip id={`tooltip-delete-${optionGroup._id}`}>
                                            Delete Option Group
                                          </Tooltip>
                                        }
                                      >
                                        <Button
                                          size="sm"
                                          {...catalogueGate.disabledProps}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteOptionGroup(optionGroup._id);
                                          }}
                                          style={{
                                            borderRadius: "8px",
                                            padding: "6px 12px",
                                            background: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
                                            border: "none",
                                            boxShadow: "0 2px 6px rgba(220, 53, 69, 0.3)",
                                          }}
                                        >
                                          <MdDelete size={16} color="#ffffff" />
                                        </Button>
                                      </OverlayTrigger>
                                    </div>
                                    
                                    <h5 style={{ 
                                      fontWeight: "600", 
                                      fontSize: "16px",
                                      marginBottom: "8px",
                                      color: "#1a1a2e"
                                    }}>
                                      {optionGroup.name}
                                    </h5>
                                    
                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                      {isRequired && (
                                        <Badge 
                                          bg={isPickOne ? "success" : "warning"} 
                                          style={{ 
                                            fontSize: "11px", 
                                            fontWeight: "500",
                                            padding: "4px 8px",
                                            borderRadius: "6px"
                                          }}
                                        >
                                          {isPickOne ? "Pick One" : "Required"}
                                        </Badge>
                                      )}
                                      {!isRequired && (
                                        <Badge 
                                          bg="secondary" 
                                          style={{ 
                                            fontSize: "11px", 
                                            fontWeight: "500",
                                            padding: "4px 8px",
                                            borderRadius: "6px"
                                          }}
                                        >
                                          Optional
                                        </Badge>
                                      )}
                                      <Badge 
                                        bg="light" 
                                        text="dark"
                                        style={{ 
                                          fontSize: "11px", 
                                          fontWeight: "500",
                                          padding: "4px 8px",
                                          borderRadius: "6px",
                                          border: "1px solid #e9ecef"
                                        }}
                                      >
                                        {optionCount} option{optionCount !== 1 ? "s" : ""}
                                      </Badge>
                                    </div>
                                    
                                    <div style={{ 
                                      background: "#f8f9fa", 
                                      borderRadius: "10px", 
                                      padding: "12px",
                                      fontSize: "13px",
                                      color: "#495057"
                                    }}>
                                      <div className="d-flex justify-content-between">
                                        <span>Min selections:</span>
                                        <strong>{optionGroup.minOptions}</strong>
                                      </div>
                                      <div className="d-flex justify-content-between mt-1">
                                        <span>Max selections:</span>
                                        <strong>{optionGroup.maxOptions === 0 ? "Unlimited" : optionGroup.maxOptions}</strong>
                                      </div>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                      )}
                    </div>
                  )}
                  {activeNav === "options" && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                          <h4 style={{ fontWeight: "700", color: "#E91E63", marginBottom: "4px" }}>
                            Options
                          </h4>
                          <p style={{ color: "#6c757d", fontSize: "14px", marginBottom: 0 }}>
                            Create individual options that can be added to option groups (e.g., "Extra Dosage", "Bulk Pack", "Express Delivery")
                          </p>
                        </div>
                        <Button
                          variant="primary"
                          onClick={() => setShowOptionModal(true)}
                          {...catalogueGate.disabledProps}
                          style={{
                            background: "linear-gradient(135deg, #0474e3ff 0%, #0056b3 100%)",
                            border: "none",
                            boxShadow: "0 4px 12px rgba(4, 116, 227, 0.3)",
                          }}
                        >
                          <MdAdd size={18} style={{ marginRight: "6px", verticalAlign: "middle" }} />
                          Add Option
                        </Button>
                      </div>
                      
                      {options.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "60px 20px", color: "#6c757d" }}>
                          <MdSettings size={48} style={{ marginBottom: "12px", opacity: 0.5 }} />
                          <h5 style={{ fontWeight: "600" }}>No Options Yet</h5>
                          <p>Create options to use in option groups</p>
                        </div>
                      ) : (
                        <Row>
                          {options.map((option) => (
                            <Col md={6} lg={4} key={option._id} className="mb-3">
                              <Card
                                style={{
                                  border: "1px solid #e9ecef",
                                  borderRadius: "14px",
                                  transition: "all 0.3s ease",
                                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                                  overflow: "hidden",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = "translateY(-2px)";
                                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.1)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "translateY(0)";
                                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.04)";
                                }}
                              >
                                <Card.Body style={{ padding: "16px" }}>
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="d-flex align-items-center gap-3">
                                      <div
                                        style={{
                                          width: "40px",
                                          height: "40px",
                                          borderRadius: "10px",
                                          background: option.enabled 
                                            ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" 
                                            : "linear-gradient(135deg, #6c757d 0%, #495057 100%)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color: "white",
                                          fontSize: "14px",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {option.name.charAt(0).toUpperCase()}
                                      </div>
                                      <div>
                                        <h6 style={{ 
                                          fontWeight: "600", 
                                          fontSize: "15px",
                                          marginBottom: "4px",
                                          color: option.enabled ? "#1a1a2e" : "#6c757d"
                                        }}>
                                          {option.name}
                                          {!option.enabled && (
                                            <Badge 
                                              bg="secondary" 
                                              style={{ 
                                                marginLeft: "8px", 
                                                fontSize: "10px",
                                                fontWeight: "500"
                                              }}
                                            >
                                              Disabled
                                            </Badge>
                                          )}
                                        </h6>
                                        <span style={{
                                          fontSize: "14px",
                                          fontWeight: "600",
                                          color: option.price === 0 ? "#10b981" : "#0474e3ff",
                                        }}>
                                          {option.price === 0 
                                            ? "Free" 
                                            : new Intl.NumberFormat("en-UG", {
                                                style: "currency",
                                                currency: "UGX",
                                              }).format(option.price)
                                          }
                                        </span>
                                      </div>
                                    </div>
                                    <div className="d-flex gap-2">
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Edit Option</Tooltip>}
                                      >
                                        <Button
                                          size="sm"
                                          {...catalogueGate.disabledProps}
                                          onClick={() => handleEditOption(option)}
                                          style={{
                                            borderRadius: "8px",
                                            padding: "6px 12px",
                                            background: "linear-gradient(135deg, #0474e3ff 0%, #0056b3 100%)",
                                            border: "none",
                                            boxShadow: "0 2px 6px rgba(4, 116, 227, 0.3)",
                                          }}
                                        >
                                          <MdEdit size={14} color="#ffffff" />
                                        </Button>
                                      </OverlayTrigger>
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Delete Option</Tooltip>}
                                      >
                                        <Button
                                          size="sm"
                                          {...catalogueGate.disabledProps}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteOption(option._id);
                                          }}
                                          style={{
                                            borderRadius: "8px",
                                            padding: "6px 12px",
                                            background: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
                                            border: "none",
                                            boxShadow: "0 2px 6px rgba(220, 53, 69, 0.3)",
                                          }}
                                        >
                                          <MdDelete size={14} color="#ffffff" />
                                        </Button>
                                      </OverlayTrigger>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </div>
                  )}
                  {activeNav === "branches" && branchDirectory.hasBranches && (
                    <BranchAvailabilityGrid
                      vendorUserId={vendorId}
                      products={(sections || []).flatMap((section) => section.products || [])}
                      onChanged={branchOff.refresh}
                    />
                  )}
                  {activeNav === "simulation" && (
                    <div style={{ padding: "10px" }}>
                      <p style={{ color: "#002333", marginBottom: "20px" }}>
                        This section simulates how the products and services
                        will look for web users in production.
                      </p>
                      {sections
                        .filter(
                          (section) =>
                            section.products &&
                            section.products.some((product) => product.enabled)
                        )
                        .map((section) => (
                          <div
                            key={section._id}
                            id={`section-${section._id}`}
                            style={{ marginBottom: "20px" }}
                          >
                            <div style={{ marginBottom: "10px" }}>
                              <h2
                                style={{ fontSize: "1.2em", color: "#E91E63" }}
                              >
                                {section.title}
                              </h2>
                              {section.description && (
                                <p
                                  style={{
                                    fontSize: "0.9em",
                                    color: "#6c757d",
                                  }}
                                >
                                  {section.description}
                                </p>
                              )}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "15px",
                                justifyContent: "flex-start",
                              }}
                            >
                              {section.products &&
                                section.products
                                  .filter((product) => product.enabled)
                                  .map((product) => (
                                    <div
                                      key={product._id}
                                      style={{
                                        width: "200px",
                                        minWidth: "200px",
                                        maxWidth: "200px",
                                        border: "1px solid #e9ecef",
                                        padding: "12px",
                                        textAlign: "left",
                                        boxShadow:
                                          "0 2px 8px rgba(0, 0, 0, 0.1)",
                                        borderRadius: "12px",
                                        backgroundColor: "white",
                                        transition: "all 0.3s ease",
                                        cursor: "pointer",
                                        display: "flex",
                                        flexDirection: "column",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.transform =
                                          "translateY(-4px)";
                                        e.currentTarget.style.boxShadow =
                                          "0 4px 16px rgba(233, 30, 99, 0.2)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform =
                                          "translateY(0)";
                                        e.currentTarget.style.boxShadow =
                                          "0 2px 8px rgba(0, 0, 0, 0.1)";
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: "100%",
                                          height: "180px",
                                          overflow: "hidden",
                                          borderRadius: "8px",
                                          marginBottom: "10px",
                                          backgroundColor: "#f8f9fa",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <img
                                          src={
                                            product.image || placeholderImage
                                          }
                                          alt={product.name}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                          }}
                                        />
                                      </div>
                                      <div
                                        style={{
                                          flex: 1,
                                          display: "flex",
                                          flexDirection: "column",
                                        }}
                                      >
                                        <h5
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            margin: "0 0 6px 0",
                                            color: "#333",
                                            lineHeight: "1.3",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                          }}
                                        >
                                          {product.name}
                                        </h5>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6c757d",
                                            marginBottom: "8px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            minHeight: "32px",
                                          }}
                                        >
                                          {product.description ||
                                            "No description available."}
                                        </div>
                                        <div style={{ marginTop: "auto" }}>
                                          <p
                                            style={{
                                              fontSize: "16px",
                                              color: "#E91E63",
                                              fontWeight: "700",
                                              margin: "0 0 6px 0",
                                            }}
                                          >
                                            {new Intl.NumberFormat("en-UG", {
                                              style: "currency",
                                              currency: "UGX",
                                            }).format(product.price)}
                                          </p>
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: "4px",
                                              flexWrap: "wrap",
                                            }}
                                          >
                                            {product.requiresPrescription && (
                                              <Badge
                                                bg="danger"
                                                style={{
                                                  fontSize: "9px",
                                                  padding: "2px 6px",
                                                }}
                                              >
                                                Rx
                                              </Badge>
                                            )}
                                            {product.productType ===
                                              "SERVICE" && (
                                              <Badge
                                                bg="info"
                                                style={{
                                                  fontSize: "9px",
                                                  padding: "2px 6px",
                                                }}
                                              >
                                                Service
                                              </Badge>
                                            )}
                                            {product.ageRestriction > 0 && (
                                              <Badge
                                                bg="warning"
                                                text="dark"
                                                style={{
                                                  fontSize: "9px",
                                                  padding: "2px 6px",
                                                }}
                                              >
                                                {product.ageRestriction}+
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                  {activeNav === "how-it-works" && (
                    <div style={{ padding: "20px" }}>
                      <div style={{ marginBottom: "30px" }}>
                        <h4 style={{ color: "#E91E63", marginBottom: "10px" }}>
                          Welcome to Healthcare Menu Management
                        </h4>
                        <p style={{ color: "#6c757d", fontSize: "14px" }}>
                          This guide will help you set up and manage your healthcare products and services effectively.
                        </p>
                      </div>
                      <Accordion defaultActiveKey="0">
                        <Accordion.Item eventKey="0">
                          <Accordion.Header>
                            <strong>1. Getting Started</strong>
                          </Accordion.Header>
                          <Accordion.Body>
                            <p>The Healthcare Menu Management system allows you to organize and manage your products and services efficiently.</p>
                            <ul>
                              <li><strong>Sections:</strong> Categories that group related products/services (e.g., "Medications", "Consultations", "Medical Equipment")</li>
                              <li><strong>Products:</strong> Physical items like medicines, medical supplies, or equipment</li>
                              <li><strong>Services:</strong> Appointments, consultations, or procedures</li>
                            </ul>
                            <p style={{ marginTop: "10px", padding: "10px", backgroundColor: "#fff3e0", borderLeft: "4px solid #E91E63", fontSize: "13px" }}>
                              <strong>Tip:</strong> Start by creating sections first, then add products/services to those sections.
                            </p>
                          </Accordion.Body>
                        </Accordion.Item>

                        <Accordion.Item eventKey="1">
                          <Accordion.Header>
                            <strong>2. Creating Sections</strong>
                          </Accordion.Header>
                          <Accordion.Body>
                            <p>Sections help organize your menu into logical categories.</p>
                            <ol>
                              <li>Click the <strong>"+ Create Section"</strong> button at the top right</li>
                              <li>Enter a descriptive section title (e.g., "Prescription Medications", "Lab Tests")</li>
                              <li>Add an optional description to explain what this section contains</li>
                              <li>Select your healthcare provider from the dropdown</li>
                              <li>Click <strong>"Create Section"</strong> to save</li>
                            </ol>
                            <p style={{ marginTop: "10px", fontSize: "13px", color: "#666" }}>
                              You can edit or delete sections anytime using the edit/delete icons next to each section name.
                            </p>
                          </Accordion.Body>
                        </Accordion.Item>

                        <Accordion.Item eventKey="2">
                          <Accordion.Header>
                            <strong>3. Adding Products & Services</strong>
                          </Accordion.Header>
                          <Accordion.Body>
                            <p>Once you have sections created, you can add products or services to them.</p>
                            <ol>
                              <li>Navigate to the section where you want to add an item</li>
                              <li>Click <strong>"+ Add Product/Service"</strong> within that section</li>
                              <li>Fill in the required information:
                                <ul style={{ marginTop: "8px" }}>
                                  <li>Name (required)</li>
                                  <li>Description</li>
                                  <li>Price in UGX</li>
                                  <li>Image (upload or URL)</li>
                                  <li>Product Type (Product or Service)</li>
                                </ul>
                              </li>
                              <li>Set healthcare-specific options (prescriptions, age restrictions, etc.)</li>
                              <li>Enable/disable the item for customer visibility</li>
                            </ol>
                          </Accordion.Body>
                        </Accordion.Item>

                        <Accordion.Item eventKey="3">
                          <Accordion.Header>
                            <strong>4. Product Types: PRODUCT vs SERVICE</strong>
                          </Accordion.Header>
                          <Accordion.Body>
                            <h6 style={{ color: "#E91E63" }}>PRODUCT</h6>
                            <p>Physical items that customers can purchase:</p>
                            <ul>
                              <li>Medications (prescription and over-the-counter)</li>
                              <li>Medical supplies (bandages, thermometers)</li>
                              <li>Health equipment (blood pressure monitors)</li>
                              <li>Uses <strong>"Stock"</strong> to track inventory count</li>
                            </ul>
                            
                            <h6 style={{ color: "#E91E63", marginTop: "15px" }}>SERVICE</h6>
                            <p>Appointments, consultations, or procedures:</p>
                            <ul>
                              <li>Doctor consultations</li>
                              <li>Lab tests</li>
                              <li>Diagnostic procedures</li>
                              <li>Uses <strong>"Slots"</strong> to manage availability</li>
                            </ul>
                          </Accordion.Body>
                        </Accordion.Item>

                        <Accordion.Item eventKey="4">
                          <Accordion.Header>
                            <strong>5. Healthcare-Specific Features</strong>
                          </Accordion.Header>
                          <Accordion.Body>
                            <p>Healthcare products and services have special compliance features:</p>
                            
                            <div style={{ marginBottom: "15px" }}>
                              <h6 style={{ color: "#d32f2f" }}>Prescription Required (Rx)</h6>
                              <p>Mark items that require a valid prescription. Customers will be prompted to upload prescription documents.</p>
                            </div>
                            
                            <div style={{ marginBottom: "15px" }}>
                              <h6 style={{ color: "#f57c00" }}>Age Restriction</h6>
                              <p>Set minimum age requirements (e.g., 18+ for certain medications). The system will verify customer age before purchase.</p>
                            </div>
                            
                            <div style={{ marginBottom: "15px" }}>
                              <h6 style={{ color: "#424242" }}>Controlled Substance</h6>
                              <p>Flag items that are controlled substances for additional verification and compliance tracking.</p>
                            </div>
                            
                            <p style={{ marginTop: "15px", padding: "10px", backgroundColor: "#ffebee", borderLeft: "4px solid #d32f2f", fontSize: "13px" }}>
                              <strong>Important:</strong> Always ensure compliance with local healthcare regulations when marking these flags.
                            </p>
                          </Accordion.Body>
                        </Accordion.Item>

                        <Accordion.Item eventKey="5">
                          <Accordion.Header>
                            <strong>6. Managing Inventory</strong>
                          </Accordion.Header>
                          <Accordion.Body>
                            <h6 style={{ color: "#E91E63" }}>For Products (Physical Items)</h6>
                            <ul>
                              <li><strong>Count in Stock:</strong> Set the number of units available</li>
                              <li>System automatically decreases stock when orders are placed</li>
                              <li>Products show as "Out of Stock" when count reaches zero</li>
                              <li>Update stock levels regularly to maintain accuracy</li>
                            </ul>
                            
                            <h6 style={{ color: "#E91E63", marginTop: "15px" }}>For Services (Appointments/Consultations)</h6>
                            <ul>
                              <li><strong>Available Slots:</strong> Set how many appointments you can handle</li>
                              <li>Slots decrease as customers book appointments</li>
                              <li>Service becomes unavailable when all slots are booked</li>
                              <li>Reset or increase slots based on your scheduling capacity</li>
                            </ul>
                            
                            <p style={{ marginTop: "15px", padding: "10px", backgroundColor: "#e8f5e9", borderLeft: "4px solid #4caf50", fontSize: "13px" }}>
                              <strong>Best Practice:</strong> Monitor your inventory/slots regularly to avoid overselling or missed appointments.
                            </p>
                          </Accordion.Body>
                        </Accordion.Item>

                        <Accordion.Item eventKey="6">
                          <Accordion.Header>
                            <strong>7. Using the Simulation Tab</strong>
                          </Accordion.Header>
                          <Accordion.Body>
                            <p>The Simulation tab shows exactly how your products and services will appear to customers.</p>
                            <ol>
                              <li>Click on the <strong>"Simulation"</strong> tab</li>
                              <li>View your sections and products as customers will see them</li>
                              <li>Check that:
                                <ul style={{ marginTop: "8px" }}>
                                  <li>Images display correctly and are appropriately sized</li>
                                  <li>Descriptions are clear and informative</li>
                                  <li>Prices are formatted correctly</li>
                                  <li>Healthcare flags (Rx, age restrictions) are visible</li>
                                  <li>Only enabled products are shown</li>
                                </ul>
                              </li>
                              <li>Make adjustments in the Menu Overview tab if needed</li>
                            </ol>
                            <p style={{ marginTop: "10px", fontSize: "13px", color: "#666" }}>
                              The simulation preview updates in real-time as you make changes to your menu.
                            </p>
                          </Accordion.Body>
                        </Accordion.Item>
                      </Accordion>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Bulk import. The vendor id is the same `healthcareId` every other
          call on this page uses, not the signed-in user id: sections and
          products hang off the healthcare vendor document. */}
      <HealthcareImportModal
        show={showImportModal}
        vendor={{
          _id: healthcareVendors[0]?.healthcareId,
          name: healthcareVendors[0]?.name,
        }}
        existingSections={Array.isArray(sections) ? sections : []}
        onClose={() => setShowImportModal(false)}
        onImported={() => {
          const healthcareId = healthcareVendors[0]?.healthcareId;
          if (healthcareId) dispatch(fetchHealthcareSections(healthcareId));
        }}
      />

      {/* Create Section Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Section</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleMenuSectionSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Section Title *</Form.Label>
              <Form.Control
                type="text"
                value={sectionTitle}
                onChange={(e) => setSectionTitle(e.target.value)}
                placeholder="e.g., Medications, Consultations, Medical Equipment"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={sectionDescription}
                onChange={(e) => setSectionDescription(e.target.value)}
                placeholder="Optional description for this section"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Healthcare Provider *</Form.Label>
              <AppSelect block
                value={selectedHealthcareId}
                onChange={(e) => setSelectedHealthcareId(e.target.value)}
                required
              >
                <option value="">Select healthcare provider</option>
                {healthcareVendors.map((vendor) => (
                  <option key={vendor._id} value={vendor.healthcareId}>
                    {vendor.name}
                  </option>
                ))}
              </AppSelect>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                Create Section
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Section Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Section</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSectionEditSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Section Title *</Form.Label>
              <Form.Control
                type="text"
                value={sectionTitle}
                onChange={(e) => setSectionTitle(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={sectionDescription}
                onChange={(e) => setSectionDescription(e.target.value)}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                Update Section
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the section "
          <strong>{sectionToDelete?.title}</strong>"? This will also delete all
          products/services in this section.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteSection}>
            Delete Section
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal for creating a new option */}
      <Modal show={showOptionModal} onHide={() => setShowOptionModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create a new option</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="optionName">
              <Form.Label>Option name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter option name..."
                value={optionName}
                onChange={(e) => setOptionName(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="optionPrice">
              <Form.Label>Option price</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter option price..."
                value={optionPrice}
                onChange={(e) => setOptionPrice(parseFloat(e.target.value))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOptionModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleOptionSubmit}>
            Create option
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal for creating or editing an option group */}
      <Modal show={showOptionGroupModal} onHide={handleCloseOptionGroupModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #E91E63 0%, #C2185B 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}>
              <MdTune size={20} />
            </div>
            {currentOptionGroupId
              ? "Edit Option Group"
              : "Create Option Group"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "24px" }}>
          <Form>
            {/* Quick Presets Section */}
            <div style={{ 
              marginBottom: "24px", 
              padding: "16px", 
              background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)", 
              borderRadius: "12px",
              border: "1px solid #e9ecef"
            }}>
              <Form.Label style={{ fontWeight: "600", color: "#E91E63", marginBottom: "12px", display: "block" }}>
                Quick Presets
              </Form.Label>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    setMinOptions(1);
                    setMaxOptions(1);
                  }}
                  style={{ 
                    borderRadius: "20px", 
                    padding: "8px 18px",
                    fontWeight: "600",
                    fontSize: "13px",
                    border: "none",
                    color: "#ffffff",
                    background: minOptions === 1 && maxOptions === 1 ? "#047857" : "#059669",
                  }}
                >
                  Pick One
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setMinOptions(0);
                    setMaxOptions(3);
                  }}
                  style={{ 
                    borderRadius: "20px", 
                    padding: "8px 18px",
                    fontWeight: "600",
                    fontSize: "13px",
                    border: "none",
                    color: "#ffffff",
                    background: minOptions === 0 && maxOptions === 3 ? "#0f172a" : "#1e3a5f",
                  }}
                >
                  Optional (Up to 3)
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setMinOptions(1);
                    setMaxOptions(0);
                  }}
                  style={{ 
                    borderRadius: "20px", 
                    padding: "8px 18px",
                    fontWeight: "600",
                    fontSize: "13px",
                    border: "none",
                    color: "#ffffff",
                    background: minOptions === 1 && maxOptions === 0 ? "#b45309" : "#d97706",
                  }}
                >
                  Required (Unlimited)
                </Button>
              </div>
            </div>

            <Form.Group controlId="optionGroupName" className="mb-3">
              <Form.Label style={{ fontWeight: "600", color: "#495057" }}>Option Group Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Add-ons, Dosage, Quantity..."
                value={optionGroupName}
                onChange={(e) => setOptionGroupName(e.target.value)}
                style={{
                  borderRadius: "10px",
                  padding: "12px 16px",
                  border: "1px solid #e9ecef",
                  boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                }}
              />
            </Form.Group>

            <Form.Group controlId="optionSelect" className="mb-3">
              <Form.Label style={{ fontWeight: "600", color: "#495057" }}>Select Options</Form.Label>
              <Select
                isMulti
                options={options.map((option) => ({
                  value: option._id,
                  label: `${option.name} - ${new Intl.NumberFormat("en-UG", {
                    style: "currency",
                    currency: "UGX",
                  }).format(option.price)}`,
                  mandatory: false,
                }))}
                value={selectedOptions}
                onChange={setSelectedOptions}
                placeholder="Search and select options..."
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderRadius: "10px",
                    border: "1px solid #e9ecef",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                    padding: "4px",
                  }),
                  multiValue: (provided) => ({
                    ...provided,
                    backgroundColor: "#fce7f3",
                    borderRadius: "6px",
                  }),
                }}
              />
              {options.length === 0 && (
                <Form.Text style={{ color: "#6c757d", fontSize: "12px" }}>
                  No options available. Create options first in the "Options" tab.
                </Form.Text>
              )}
            </Form.Group>

            {/* Selection Rules */}
            <div style={{ 
              background: "#f8f9fa", 
              borderRadius: "12px", 
              padding: "16px",
              marginBottom: "16px"
            }}>
              <Form.Label style={{ fontWeight: "600", color: "#E91E63", marginBottom: "12px", display: "block" }}>
                Selection Rules
              </Form.Label>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="minOptions">
                    <Form.Label style={{ fontSize: "13px", color: "#495057" }}>
                      Minimum Selections
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Set to 1 or more to make this group required</Tooltip>}
                      >
                        <FaInfoCircle style={{ marginLeft: "6px", color: "#6c757d", cursor: "help" }} size={12} />
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="0"
                      value={minOptions}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        setMinOptions(value < 0 ? 0 : value);
                      }}
                      min="0"
                      style={{
                        borderRadius: "8px",
                        border: "1px solid #e9ecef",
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="maxOptions">
                    <Form.Label style={{ fontSize: "13px", color: "#495057" }}>
                      Maximum Selections
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Set to 0 for unlimited selections</Tooltip>}
                      >
                        <FaInfoCircle style={{ marginLeft: "6px", color: "#6c757d", cursor: "help" }} size={12} />
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="0 = Unlimited"
                      value={maxOptions}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        setMaxOptions(value < 0 ? 0 : value);
                      }}
                      min="0"
                      style={{
                        borderRadius: "8px",
                        border: "1px solid #e9ecef",
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Dynamic hint based on current settings */}
              <Alert 
                variant={minOptions === 1 && maxOptions === 1 ? "success" : minOptions > 0 ? "warning" : "info"}
                style={{ 
                  marginTop: "12px", 
                  marginBottom: 0, 
                  fontSize: "13px",
                  borderRadius: "8px",
                  padding: "10px 14px"
                }}
              >
                <strong>
                  {minOptions === 1 && maxOptions === 1 
                    ? "Pick One: " 
                    : minOptions > 0 
                      ? "Required: " 
                      : "Optional: "}
                </strong>
                {minOptions === 1 && maxOptions === 1 
                  ? "Customer must select exactly one option"
                  : minOptions > 0 && maxOptions === 0
                    ? `Customer must select at least ${minOptions} option(s), no limit on maximum`
                    : minOptions > 0 && maxOptions > 0
                      ? `Customer must select between ${minOptions} and ${maxOptions} option(s)`
                      : maxOptions > 0
                        ? `Customer can optionally select up to ${maxOptions} option(s)`
                        : "Customer can optionally select any number of options"}
              </Alert>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseOptionGroupModal}
            disabled={optionLoading}
          >
            {optionLoading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ color: "black" }}
                />
                <span style={{ color: "black" }}> Updating...</span>
              </>
            ) : (
              "Discard Changes"
            )}
          </Button>
          <Button
            variant="success"
            onClick={handleOptionGroupSubmit}
            disabled={optionLoading}
          >
            {optionLoading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ color: "black" }}
                />
                <span style={{ color: "black" }}> Updating...</span>
              </>
            ) : currentOptionGroupId ? (
              "Update Option Group"
            ) : (
              "Create Option Group"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal for delete option group confirmation */}
      <Modal
        show={showDeleteOptionGroupModal}
        onHide={() => setShowDeleteOptionGroupModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this option group? This action cannot
          be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteOptionGroupModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDeleteOptionGroup}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal for delete option confirmation */}
      <Modal
        show={showOptionDeleteModal}
        onHide={() => setShowOptionDeleteModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this option? This action cannot be
          undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowOptionDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDeleteOption}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal for editing an option */}
      <Modal
        show={showEditOptionModal}
        onHide={() => setShowEditOptionModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Option</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="editOptionName">
              <Form.Label>Option Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter option name..."
                value={editOptionName}
                onChange={(e) => setEditOptionName(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="editOptionPrice">
              <Form.Label>Option Price</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter option price..."
                value={editOptionPrice}
                onChange={(e) => setEditOptionPrice(parseFloat(e.target.value))}
              />
            </Form.Group>
            <Form.Group controlId="editOptionEnabled">
              <Form.Label>Enabled</Form.Label>
              <Switch
                checked={editOptionEnabled}
                onChange={(e) => setEditOptionEnabled(e.target.checked)}
                color="primary"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowEditOptionModal(false)}
          >
            Cancel
          </Button>
          <Button variant="success" onClick={handleEditOptionSubmit}>
            Save changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* What an outlet sells: the one picker, shared with the restaurant consoles. */}
      <OutletMenuItemsModal
        show={showOutletProductsModal}
        outlet={selectedOutlet}
        sections={sections}
        onHide={() => setShowOutletProductsModal(false)}
        onSave={handleSaveOutletProducts}
        itemsLabel="Products"
        placeholderImage={placeholderImage}
        // Pharmacy branches sell every product (4 Oct 2026); this saved list
        // does not hide anything from shoppers, so the picker says so.
        note="Shoppers still see every product at this branch. To stop selling a product here, use the Branches tab."
      />

      {/* Modal for managing outlet business hours */}
      <Modal
        show={showOutletHoursModal}
        onHide={() => setShowOutletHoursModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <MdSettings style={{ marginRight: "8px", verticalAlign: "middle" }} />
            Business Hours - {selectedOutletForHours?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingOutletHours ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spinner animation="border" style={{ marginRight: "12px" }} />
              Loading business hours...
            </div>
          ) : (
            <div>
              <Alert variant="info" style={{ marginBottom: "20px" }}>
                <strong style={{ color: "#dd0000ff", fontSize: "12px" }}>ℹ️ Business Hours</strong>
                <p style={{ marginBottom: 0, marginTop: "8px", color: "#242627ff", fontSize: "12px" }}>
                  Set custom business hours for this outlet. If not set, it will inherit hours from the parent healthcare vendor.
                </p>
              </Alert>
              {outletHours && (
                <OpenHoursManager
                  vendorLabel="pharmacy"
                  location={selectedOutletForHours?._id || null}
                  isLoading={isSavingOutletHours}
                  // No tradingPolicy here: an outlet with no hours of its own
                  // inherits the parent pharmacy's schedule server-side.
                  allowTimezoneEdit={false}
                  openHours={outletHours.openHours}
                  onSave={(openHoursData) => {
                    // OpenHoursManager only returns openHours, wrap it with existing specialHours and timezone
                    handleSaveOutletHours({
                      openHours: openHoursData,
                      specialHours: outletHours.specialHours || [],
                      timezone: outletHours.timezone || "Africa/Kampala",
                    });
                  }}
                />
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowOutletHoursModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal for confirming outlet activation/deactivation */}
      <Modal
        show={showOutletConfirmModal}
        onHide={() => setShowOutletConfirmModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton style={{ borderBottom: "1px solid #e9ecef" }} className="outlet-confirm-modal-header">
          <Modal.Title>
            Confirm Action
          </Modal.Title>
        </Modal.Header>
        <style>{`
          .outlet-confirm-modal-header .btn-close {
            filter: brightness(0) invert(1);
          }
        `}</style>
        <Modal.Body style={{ padding: "24px" }}>
          <div style={{ textAlign: "center", marginBottom: "16px" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                background: outletToToggle?.isActive ? "#fff3cd" : "#d4edda",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <MdStore
                size={28}
                style={{
                  color: outletToToggle?.isActive ? "#ff9800" : "#4caf50",
                }}
              />
            </div>
            <h5 style={{ fontWeight: "600", marginBottom: "8px", color: "#1a1a2e" }}>
              {outletToToggle?.isActive ? "Deactivate" : "Activate"} Outlet?
            </h5>
            <p style={{ color: "#6c757d", fontSize: "14px", marginBottom: 0 }}>
              Are you sure you want to{" "}
              <strong>
                {outletToToggle?.isActive ? "deactivate" : "activate"}
              </strong>{" "}
              <strong>{outletToToggle?.name}</strong>?
            </p>
          </div>

          {outletToToggle?.isActive ? (
            <Alert
              variant="warning"
              style={{
                marginTop: "16px",
                marginBottom: 0,
                fontSize: "13px",
                borderRadius: "8px",
              }}
            >
              <strong>⚠️ Warning</strong>
              <p style={{ marginBottom: 0, marginTop: "8px" }}>
                Deactivating this outlet will make it unavailable for customers to place orders. Existing orders may be affected.
              </p>
            </Alert>
          ) : (
            <Alert
              variant="success"
              style={{
                marginTop: "16px",
                marginBottom: 0,
                fontSize: "13px",
                borderRadius: "8px",
              }}
            >
              <strong style={{ color: "#1a1a2e" }}>✅ Confirmation</strong>
              <p style={{ marginBottom: 0, marginTop: "8px", color: "#1a1a2e" }}>
                Activating this outlet will make it available for customers to place orders.
              </p>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #e9ecef", padding: "16px 24px" }}>
          <Button
            variant="secondary"
            onClick={() => {
              setShowOutletConfirmModal(false);
              setOutletToToggle(null);
            }}
            disabled={isTogglingOutlet}
            style={{ borderRadius: "8px", fontWeight: "500" }}
          >
            Cancel
          </Button>
          <Button
            variant={outletToToggle?.isActive ? "danger" : "success"}
            onClick={confirmToggleOutletActivation}
            disabled={isTogglingOutlet}
            style={{
              borderRadius: "8px",
              fontWeight: "500",
              background: outletToToggle?.isActive
                ? "linear-gradient(135deg, #dc3545 0%, #c82333 100%)"
                : "linear-gradient(135deg, #0a7222ff 0%, #0a6b4dff 100%)",
              border: "none",
            }}
          >
            {isTogglingOutlet ? (
              <>
                <Spinner animation="border" size="sm" style={{ marginRight: "6px" }} />
                {outletToToggle?.isActive ? "Deactivating..." : "Activating..."}
              </>
            ) : (
              <>
                {outletToToggle?.isActive ? "Deactivate" : "Activate"} Outlet
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Floating Image Preview */}
      <style>{`@keyframes hvm-fadeIn { from { opacity:0; transform:scale(0.92); } to { opacity:1; transform:scale(1); } }`}</style>
      {hoveredImage && (
        <div
          style={{
            position: "fixed",
            left: Math.min(imagePreviewPosition.x, window.innerWidth - 420),
            top: Math.max(20, Math.min(imagePreviewPosition.y, window.innerHeight - 420)),
            zIndex: 9999,
            pointerEvents: "none",
            animation: "hvm-fadeIn 0.18s ease-out",
          }}
        >
          <div
            style={{
              width: "400px",
              height: "400px",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              border: "4px solid white",
              background: "#f8f9fa",
            }}
          >
            <img
              src={hoveredImage}
              alt="Product preview"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthcareVendorMenu;
